import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start as true
  const [initialized, setInitialized] = useState(false);

  // Ensure axios is configured to send credentials
  axios.defaults.withCredentials = true;

  // Check auth on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        await checkAuth();
      } catch (error) {
        console.error('Initial auth check failed:', error);
      } finally {
        setIsLoading(false);
        setInitialized(true);
      }
    };

    initAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authService.getCurrentUser();
      
      if (response.success && response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        return { success: true, user: response.data };
      } else {
        setUser(null);
        setIsAuthenticated(false);
        return { success: false };
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      return { success: false };
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await authService.register(userData);
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return {
          success: true,
          message: 'Registration successful!',
          user: response.data.user,
        };
      }
      
      return {
        success: false,
        message: response.message || 'Registration failed',
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      return {
        success: false,
        message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      setIsLoading(true);
      const response = await authService.login(email, password, rememberMe);
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        console.log('Login successful, user set in context:', response.data.user);
        return {
          success: true,
          message: 'Login successful!',
          user: response.data.user,
        };
      }
      
      return {
        success: false,
        message: response.message || 'Login failed',
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed. Please check your credentials.';
      return {
        success: false,
        message,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      
      if (response.success && response.data) {
        setUser(response.data);
        return {
          success: true,
          message: 'Profile updated successfully!',
        };
      }
      
      return {
        success: false,
        message: response.message || 'Update failed',
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile.';
      return {
        success: false,
        message,
      };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      const response = await authService.changePassword(oldPassword, newPassword);
      
      if (response.success) {
        return {
          success: true,
          message: 'Password changed successfully!',
        };
      }
      
      return {
        success: false,
        message: response.message || 'Password change failed',
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password.';
      return {
        success: false,
        message,
      };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    initialized,
    register,
    login,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};